import React from 'react';
import { useParams, Link } from 'react-router';
import { publicAsset } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Printer } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { fmtDate as libFmtDate, fmtDateTime, toDateInputValue } from '@/lib/format-date';
import { isScheduled, statusChipClass } from '@/pages/my-dispatch/status';

interface PreviewPayload {
  sheet: {
    _id: string;
    id?: string;
    name: string | null;
    date: string | null;
    status: string | null;
    notes: string | null;
    branch?: { name: string } | null;
  };
  assets: Array<{
    _id: string;
    fleetNumber: string;
    registrationNumber?: string | null;
    make?: string | null;
    model?: string | null;
    company?: { name: string } | null;
    vehicleGroup?: { group: string } | null;
    branch?: { name: string } | null;
  }>;
  jobs: Array<{
    _id: string;
    jobNumber?: string | null;
    customer?: string | null;
    jobDetails?: string | null;
    info?: string | null;
    status?: string | null;
    start?: string | null;
    end?: string | null;
    phoneNumber?: string | null;
    completionNotes?: string | null;
    asset?: { fleetNumber: string; registrationNumber?: string | null } | null;
    driver?: { fullName?: string | null } | null;
    address?: {
      addressLine1?: string | null;
      addressLine2?: string | null;
      city?: string | null;
    } | null;
    products: Array<{
      itemCode?: string | null;
      description?: string | null;
      quantity?: number | null;
      unit?: string | null;
    }>;
  }>;
}

// 24-hour, Pacific/Auckland — matches the dashboard's 24-hour timeline and
// keeps the depot wall-clock independent of viewer/server tz.
const NZ_TIME_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Pacific/Auckland', hour: '2-digit', minute: '2-digit', hour12: false });
function fmtTime(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = NZ_TIME_FMT.formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const hour = get('hour');
  const h24 = hour === 24 ? 0 : hour;
  const m = get('minute');
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Local thin wrapper — keeps the existing call sites intact while the
// underlying format comes from the centralized lib helper.
function fmtDate(v?: string | null): string {
  return libFmtDate(v);
}

function fmtAddress(a: any): string {
  if (!a) return '';
  const parts = [a.addressLine1, a.addressLine2, a.city].filter(Boolean);
  return parts.join(' ');
}

/** Lazy-load html2pdf.js from cdnjs on first use — keeps it out of the
 *  main bundle since most operators just view the run sheet on screen. */
function loadHtml2Pdf(): Promise<any> {
  const w = window as any;
  if (w.html2pdf) return Promise.resolve(w.html2pdf);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
    s.onload = () => resolve((window as any).html2pdf);
    s.onerror = () => reject(new Error('Failed to load PDF library — check your internet connection.'));
    document.head.appendChild(s);
  });
}

function fmtJobSite(j: PreviewPayload['jobs'][number]): string {
  const bits: string[] = [];
  if (j.jobDetails) bits.push(j.jobDetails);
  else if (j.info) bits.push(j.info);
  const addr = fmtAddress(j.address);
  if (addr) bits.push(addr);
  return bits.join(' ').trim() || '—';
}

/** Multi-line product cell — one "ITEMCODE - qty" per row, mirroring the
 *  Zoho layout where multi-product loads list each line stacked. */
function ProductLines({ products }: { products: PreviewPayload['jobs'][number]['products'] }) {
  if (!products || products.length === 0) return <>—</>;
  return (
    <div className="space-y-0.5">
      {products.map((p, i) => {
        const code = p.itemCode || p.description || '—';
        const qty = p.quantity != null ? Number(p.quantity).toFixed(2) : '—';
        return <div key={i}>{code} - {qty}</div>;
      })}
    </div>
  );
}

function statusChip(s?: string | null): React.ReactNode {
  if (!s) return null;
  // Shared vocabulary — previously this knew only 'Scheduled Job' and
  // 'Complete', so an API-created job printed a plain grey "Scheduled" chip
  // while an identical Atlas-created one printed red.
  // 'Scheduled' and 'Scheduled Job' both print as "Scheduled": the long form
  // is a storage artefact, not something to show an operator on paper.
  const label = isScheduled(s) ? 'Scheduled' : s;
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusChipClass(s)}`}>
      {label}
    </span>
  );
}

export function RunSheetPrintPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['/run-sheets', id, 'preview'],
    queryFn: async () => (await api<PreviewPayload>(`/run-sheets/${id}/preview`)).data,
    enabled: !!id,
  });

  // Hooks MUST run unconditionally before any early return — putting them
  // below the `if (isLoading) return …` branches breaks the rules of
  // hooks (React sees a different hook count between renders and throws
  // "Rendered more hooks than during the previous render").
  const sheetRef = React.useRef<HTMLDivElement>(null);
  const [generatingPdf, setGeneratingPdf] = React.useState(false);

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-8 text-muted-foreground">Run sheet not found.</div>;

  const { sheet, assets, jobs } = data;

  const totalJobs = jobs.length;
  const totalQty = jobs.reduce((sum, j) => sum + j.products.reduce((s, p) => s + (p.quantity || 0), 0), 0);

  // Two-level grouping for the printed sheet:
  //   Branch (outer)  →  Asset (inner)  →  rows of dispatch jobs.
  // The dispatcher reads top-down by yard / depot first, then by truck.
  // VehicleGroup is rendered as a sub-label inside each asset header so the
  // old at-a-glance "which fleet bucket am I in" information isn't lost.
  // Assets without a branch fall under "Unassigned" so they still appear.
  const assetById = new Map<string, typeof assets[number]>();
  for (const a of assets) assetById.set(a._id, a);
  const jobsByAsset = new Map<string, typeof jobs>();
  for (const j of jobs) {
    const aid = j.asset?.fleetNumber
      ? assets.find((x) => x.fleetNumber === j.asset!.fleetNumber)?._id
      : undefined;
    const key = aid ?? '__unassigned__';
    if (!jobsByAsset.has(key)) jobsByAsset.set(key, []);
    jobsByAsset.get(key)!.push(j);
  }
  const assetsByBranch = new Map<string, typeof assets>();
  for (const a of assets) {
    const bname = a.branch?.name?.trim() || 'Unassigned';
    if (!assetsByBranch.has(bname)) assetsByBranch.set(bname, []);
    assetsByBranch.get(bname)!.push(a);
  }
  const branchNames = [...assetsByBranch.keys()].sort((x, y) => {
    if (x === 'Unassigned') return 1;
    if (y === 'Unassigned') return -1;
    return x.localeCompare(y);
  });

  // Direct PDF download — no browser print dialog. Loads html2pdf.js from
  // a CDN on first click (cached on window after), then renders the sheet
  // container into an A4 landscape PDF. Same pattern the dashboard uses
  // for its "Download PDF" button. The `sheetRef` + `generatingPdf` hooks
  // are declared above the early-returns to stay hooks-rules-compliant.
  async function downloadPdf() {
    if (!sheetRef.current || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const html2pdf = await loadHtml2Pdf();
      const safeName = (sheet.name || 'run-sheet').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
      const datePart = toDateInputValue(sheet.date ?? new Date());
      await html2pdf().set({
        // [top, right, bottom, left] — sides trimmed to 4mm so the table
        // gets ~12mm of extra width on A4 portrait; top/bottom stay at
        // 10mm so the header/footer don't crowd the page edges.
        margin:      [10, 4, 10, 4],
        filename:    `${safeName}-${datePart}.pdf`,
        image:       { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false },
        jsPDF:       { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak:   { mode: ['css', 'legacy'], avoid: ['tr', 'thead', '.break-inside-avoid'] },
      }).from(sheetRef.current).save();
    } finally {
      setGeneratingPdf(false);
    }
  }

  return (
    <div className="print-sheet mx-auto max-w-[1100px] bg-white text-zinc-900">
      {generatingPdf && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <Printer className="h-8 w-8 animate-pulse text-primary" />
            <div className="text-sm font-medium">Generating PDF…</div>
            <div className="text-xs text-muted-foreground">This can take a few seconds.</div>
          </div>
        </div>
      )}
      {/* Screen-only toolbar (hidden in print + during PDF capture) */}
      <div className="flex items-center justify-between py-3 px-4 border-b no-print">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dispatch-extras/run-sheets"><ChevronLeft className="h-4 w-4" /> Back to run sheets</Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={downloadPdf} disabled={generatingPdf}>
            <Printer className="h-4 w-4" /> {generatingPdf ? 'Generating…' : 'Download PDF'}
          </Button>
        </div>
      </div>
      <div ref={sheetRef}>

      {/* Header — centered Atlas logo, dark title, red date. Mirrors the
          Zoho run sheet structure where the logo + title + date stack
          vertically above the master table. */}
      <div className="pt-8 px-4 pb-2 text-center">
        <img src={publicAsset('atlas-logo.png')} alt="Atlas" className="mx-auto h-12 object-contain" />
        <h1 className="mt-4 text-3xl font-normal tracking-wide uppercase text-zinc-800">Tipper Fleet Run Sheet</h1>
        <div className="mt-2 text-xl font-semibold text-[#c8102e]">{fmtDate(sheet.date)}</div>
      </div>

      {/* Master table — one continuous table containing every Branch (red
          banner row) and every Asset (grey divider row) so the layout
          matches the Zoho printout. */}
      <div className="px-4 pb-8 pt-4">
        <table className="w-full border-collapse text-xs runsheet-master">
          <thead>
            <tr className="text-zinc-700 runsheet-thead">
              <th className="border py-2 px-2 font-medium text-center w-20 align-middle">Vehicle<br />Rego</th>
              <th className="border py-2 px-2 font-medium text-center w-20 align-middle">Load<br />Time</th>
              <th className="border py-2 px-2 font-medium text-center w-32 align-middle">Customer</th>
              <th className="border py-2 px-2 font-medium text-center align-middle">Job/Site/Address</th>
              <th className="border py-2 px-2 font-medium text-center w-40 align-middle">Product -<br />Quantity</th>
              <th className="border py-2 px-2 font-medium text-center w-20 align-middle">Return<br />Time</th>
              <th className="border py-2 px-2 font-medium text-center w-32 align-middle">Driver Name</th>
              <th className="border py-2 px-2 font-medium text-center w-20 align-middle no-print">Status</th>
            </tr>
          </thead>
          <tbody>
            {branchNames.map((bname) => {
              const branchAssets = assetsByBranch.get(bname) ?? [];
              return (
                <React.Fragment key={bname}>
                  {/* Red branch banner — spans every column */}
                  <tr className="bg-[#c8102e] text-white runsheet-branch break-inside-avoid">
                    <td colSpan={8} className="border border-[#c8102e] py-1.5 px-3 font-medium">{bname}</td>
                  </tr>
                  {branchAssets.map((a) => {
                    const assetJobs = jobsByAsset.get(a._id) ?? [];
                    const fleetHeader = `Fleet #${a.fleetNumber}${a.vehicleGroup?.group ? ` : ${a.vehicleGroup.group}` : ''}`;
                    return (
                      <React.Fragment key={a._id}>
                        {/* Grey fleet divider — spans every column */}
                        <tr className="bg-zinc-100 runsheet-fleet break-inside-avoid">
                          <td colSpan={8} className="border py-1.5 px-3 text-zinc-700">{fleetHeader}</td>
                        </tr>
                        {assetJobs.length === 0 ? (
                          <tr className="break-inside-avoid">
                            <td colSpan={8} className="border text-center py-3 text-zinc-400 italic text-[11px]">
                              No dispatch jobs today.
                            </td>
                          </tr>
                        ) : (
                          assetJobs.map((j) => (
                            <tr key={j._id} className="break-inside-avoid">
                              <td className="border py-2 px-2 text-center align-middle">{a.registrationNumber || '—'}</td>
                              <td className="border py-2 px-2 text-center align-middle">{fmtTime(j.start)}</td>
                              <td className="border py-2 px-2 align-middle">{j.customer || '—'}</td>
                              <td className="border py-2 px-2 align-middle whitespace-pre-wrap">
                                {fmtJobSite(j)}
                                {j.phoneNumber && (
                                  <div className="text-[10px] text-zinc-500 mt-0.5">📞 {j.phoneNumber}</div>
                                )}
                              </td>
                              <td className="border py-2 px-2 align-middle text-right">
                                <ProductLines products={j.products} />
                              </td>
                              <td className="border py-2 px-2 text-center align-middle">{fmtTime(j.end)}</td>
                              <td className="border py-2 px-2 align-middle">{j.driver?.fullName || ''}</td>
                              <td className="border py-2 px-2 text-center align-middle no-print">{statusChip(j.status)}</td>
                            </tr>
                          ))
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {totalJobs > 0 && (
          <div className="text-xs text-zinc-600 border-t pt-3">
            <strong>Total across all groups:</strong> {totalJobs} loads{totalQty > 0 ? ` · ${totalQty.toFixed(2)} qty` : ''}
          </div>
        )}

        {assets.length > 0 && (
          <div className="mt-6 text-xs text-zinc-500">
            {assets.length === 1 ? 'Fleet:' : 'Fleets:'}{' '}
            {assets.map((a) => `${a.fleetNumber}${a.registrationNumber ? ` (${a.registrationNumber})` : ''}`).join(', ')}
            {assets[0]?.company && <> · {assets[0].company.name}</>}
          </div>
        )}
      </div>
      </div>{/* /sheetRef */}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-sheet { margin: 0; max-width: none; }
          @page { size: A4 landscape; margin: 12mm; }
          body { background: white; }
          /* Preserve the Atlas-red branding on paper — Chrome / Edge /
             Safari strip backgrounds by default to save ink. The
             color-adjust properties force the red header row and the
             pink subtotal stripe to render as designed. */
          .runsheet-thead,
          .runsheet-thead th,
          .runsheet-tfoot,
          .runsheet-tfoot td {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}
